import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Mic, 
  ArrowLeft,
  MessageSquare,
  Sparkles
} from 'lucide-react';

const CreateRoom = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  // Fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center px-4 py-12">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main content card */}
      <div 
        className={`relative max-w-xl w-full transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Glass card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12 shadow-2xl">
          
          {/* Microphone icon with pulse animation */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-purple-500/30 rounded-full animate-ping" />
              <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-pulse" />
              <div className="relative bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-full">
                <Mic className="h-12 w-12 text-white" />
              </div>
            </div>
          </div>

          {/* Hero heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              POLIVOX Live Rooms
            </h1>
            <p className="text-lg text-purple-200">
              The next chapter of public conversations is almost here.
            </p>
          </div>

          {/* Divider */}
          <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-orange-500 mx-auto mb-8 rounded-full" />

          {/* Message */}
          <div className="text-center mb-8 space-y-4">
            <p className="text-gray-300 leading-relaxed">
              Live Rooms are temporarily unavailable while we refine the experience.
            </p>
            <p className="text-gray-300 leading-relaxed">
              We&apos;re building a space where conversations matter and every voice can be heard.
            </p>
            <p className="text-gray-400 text-sm">
              Thank you for being part of the journey. We&apos;ll be back soon.
            </p>
          </div>

          {/* Status badge */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-orange-300 text-sm font-medium">
                Status: Under Development
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02]"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Home
            </Button>
            
            <Button
              onClick={() => navigate('/issues')}
              variant="outline"
              className="w-full h-12 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/30 font-semibold rounded-xl transition-all duration-200"
              data-testid="explore-issues-btn"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Explore Community Issues
            </Button>
          </div>
        </div>

        {/* Bottom note */}
        <div 
          className={`text-center mt-8 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-gray-500 text-sm mb-2">
            Every great platform evolves.
          </p>
          <p className="text-gray-500 text-sm mb-3">
            We&apos;re building something worth waiting for.
          </p>
          <div className="flex items-center justify-center gap-2 text-purple-400">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">See you soon.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRoom;
